// src/shell/OfflineBanner.jsx
// App-wide persistent offline status banner.
// Shown whenever the device loses connectivity.
// Cannot be dismissed — users must always know the data age.
//
// Shows:
//   - OFFLINE mode with time since last online
//   - Which data sources were last successfully fetched
//   - Explicit "cached X hours ago" timestamp
//   - NOAA Weather Radio reminder when offline > 30 minutes

import { useState, useEffect } from 'react';
import { useAccessibilityStore } from '@store/accessibilityStore';
import { useOfflineStore }      from '@store/offlineStore';
import { useOnlineStatus }      from '@hooks';
import { useLowPowerMode }    from '@hooks/useLowPowerMode';
import { T }                    from '@components/theme';
import { NOAA_STATIONS }        from '@data/radioData';

function LowPowerToggle() {
  const lpm = useLowPowerMode();
  return (
    <div style={{
      background: lpm.active ? 'rgba(217,119,6,0.10)' : 'rgba(255,255,255,0.04)',
      border: `1px solid ${lpm.active ? 'rgba(217,119,6,0.3)' : 'rgba(255,255,255,0.07)'}`,
      borderRadius: 8, padding: '7px 10px', marginBottom: 8,
      display: 'flex', alignItems: 'center', gap: 8,
    }}>
      <span style={{ fontSize: 15 }}>{lpm.active ? '🔋' : '⚡'}</span>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 10, fontWeight: 800,
          color: lpm.active ? '#d97706' : '#9ca3af' }}>
          {lpm.active ? 'Low Power Mode ON' : 'Low Power Mode'}
          {lpm.batteryPct !== null ? ` · ${lpm.batteryPct}%` : ''}
        </div>
        <div style={{ fontSize: 9, color: '#6b7280' }}>
          {lpm.active ? 'Refresh slowed 4×, animations paused' : 'Tap to reduce battery drain'}
        </div>
      </div>
      <button onClick={lpm.toggle} style={{
        padding: '4px 10px', borderRadius: 6,
        border: `1px solid ${lpm.active ? 'rgba(217,119,6,0.4)' : 'rgba(255,255,255,0.1)'}`,
        background: lpm.active ? 'rgba(217,119,6,0.15)' : 'transparent',
        color: lpm.active ? '#d97706' : '#6b7280',
        fontSize: 10, fontWeight: 800, cursor: 'pointer', fontFamily: 'system-ui',
      }}>
        {lpm.active ? 'Disable' : 'Enable'}
      </button>
    </div>
  );
}

export function OfflineBanner({ onPanicMode }) {
  const online        = useOnlineStatus();
  const lastOfflineAt = useOfflineStore((s) => s.lastOfflineAt);
  const lastOnlineAt  = useOfflineStore((s) => s.lastOnlineAt);
  const [expanded,   setExpanded]   = useState(false);
  const [offlineMin, setOfflineMin] = useState(0);
  const bandwidthMode = useAccessibilityStore(s => s.bandwidthMode);
  const toggleBandwidth = useAccessibilityStore(s => s.toggleBandwidthMode);
  const [tick,       setTick]       = useState(0);

  // Tick every minute to update "X minutes offline" display
  useEffect(() => {
    if (online) return;
    const iv = setInterval(() => setTick(t => t + 1), 60_000);
    return () => clearInterval(iv);
  }, [online]);

  useEffect(() => {
    if (!online && lastOfflineAt) {
      setOfflineMin(Math.round((Date.now() - lastOfflineAt) / 60_000));
    }
  }, [online, lastOfflineAt, tick]);

  // Auto-expand when offline > 30 min to surface the NOAA radio reminder
  useEffect(() => {
    if (!online && offlineMin >= 30) setExpanded(true);
  }, [online, offlineMin]);

  if (online) return null;

  const offlineLabel = offlineMin < 1   ? 'Just now'
                     : offlineMin < 60  ? `${offlineMin}m`
                     : `${Math.round(offlineMin / 60)}h ${offlineMin % 60}m`;

  const lastOnlineLabel = lastOnlineAt
    ? (() => { const ms = Date.now() - lastOnlineAt; const m = Math.round(ms/60000); return m < 1 ? "just now" : m < 60 ? m + "m ago" : Math.round(m/60) + "h ago"; })()
    : 'unknown';

  return (
    <div style={{
      background: '#1c1917',
      borderBottom: `2px solid ${T.amber}`,
      flexShrink: 0,
      zIndex: 100,
    }}>
      {/* Compact bar — always visible */}
      <button
        onClick={() => setExpanded(v => !v)}
        style={{
          width: '100%', background: 'none', border: 'none',
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '7px 12px', cursor: 'pointer',
          fontFamily: T.font, textAlign: 'left',
        }}
      >
        {/* Pulsing offline dot */}
        <div style={{
          width: 8, height: 8, borderRadius: '50%',
          background: T.amber, flexShrink: 0,
          animation: 'eos-offline-pulse 2s ease-in-out infinite',
        }} />
        <div style={{ flex: 1 }}>
          <span style={{ fontSize: 11, fontWeight: 900, color: T.amber }}>
            OFFLINE
          </span>
          <span style={{ fontSize: 10, color: '#78716c', marginLeft: 6 }}>
            — showing cached data · last online {lastOnlineLabel}
          </span>
        </div>
        <span style={{ fontSize: 10, color: '#78716c', flexShrink: 0 }}>
          {expanded ? '▲' : '▼'}
        </span>
      </button>

      {/* Expanded detail */}
      {expanded && (
        <div style={{
          padding: '0 12px 10px',
          borderTop: '1px solid rgba(255,255,255,0.06)',
        }}>
          {/* Data age summary */}
          <div style={{
            display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8, marginBottom: 10,
          }}>
            {[
              { label: 'Alerts',   icon: '📰' },
              { label: 'Feed',     icon: '📋' },
              { label: 'Map',      icon: '🗺' },
              { label: 'Gas',      icon: '⛽' },
              { label: 'Contacts', icon: '📞' },
            ].map(({ label, icon }) => (
              <div key={label} style={{
                display: 'flex', alignItems: 'center', gap: 4,
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: 6, padding: '3px 7px',
                fontSize: 9, color: '#9ca3af',
              }}>
                <span>{icon}</span>
                <span style={{ fontWeight: 700 }}>{label}</span>
                <span style={{ color: '#6b7280' }}>cached</span>
              </div>
            ))}
          </div>

          {onPanicMode && (
            <button onClick={onPanicMode} style={{
              width:'100%', padding:'10px', borderRadius:10, marginBottom:8,
              border:'1.5px solid rgba(220,38,38,0.4)',
              background:'rgba(220,38,38,0.12)', color:'#fca5a5',
              fontSize:13, fontWeight:900, cursor:'pointer', fontFamily:'system-ui',
              display:'flex', alignItems:'center', justifyContent:'center', gap:8,
            }}>
              <span style={{ fontSize:16 }}>🆘</span>
              Emergency Mode — Big Buttons Only
            </button>
          )}

          {/* ─ Low Power Mode toggle ─────────────────────── */}
          <LowPowerToggle />

          {/* SOS still works */}
          <div style={{
            background: 'rgba(220,38,38,0.10)',
            border: '1px solid rgba(220,38,38,0.25)',
            borderRadius: 8, padding: '7px 10px', marginBottom: 8,
          }}>
            <div style={{ fontSize: 10, fontWeight: 800, color: '#fca5a5', marginBottom: 3 }}>
              🚨 SOS still works offline
            </div>
            <div style={{ fontSize: 9, color: '#9ca3af', lineHeight: 1.5 }}>
              SOS reports are saved and will send automatically when connectivity returns.
              If urgent, use{' '}
              <a href="/sos-offline" style={{ color: '#93c5fd', fontWeight: 800 }}>
                Offline SOS
              </a>{' '}
              to compose a GPS message via SMS — works without internet.
            </div>
          </div>

          {/* NOAA radio reminder — shown when offline > 30 min */}
          {/* SMS fallback — shown after 2+ min offline */}
          <SMSFallbackPrompt
            offlineMs={offlineMin * 60 * 1000}
            isOnline={online}
          />

          {offlineMin >= 30 && (
            <div style={{
              background: 'rgba(3,105,161,0.10)',
              border: '1px solid rgba(3,105,161,0.25)',
              borderRadius: 8, padding: '7px 10px',
            }}>
              <div style={{ fontSize: 10, fontWeight: 800, color: '#60a5fa', marginBottom: 3 }}>
                📻 NOAA Weather Radio
              </div>
              <div style={{ fontSize: 9, color: '#9ca3af', lineHeight: 1.5 }}>
                You have been offline for {offlineLabel}. For zero-connectivity emergencies,
                a battery-powered NOAA Weather Radio broadcasts 24/7 on AM/FM and receives
                official NWS alerts without internet or cell service.
                Frequency for New Orleans:{' '}
              <strong style={{ color: '#60a5fa' }}>162.400 MHz (WXL-48)</strong>
              {' · '}<a href="/radio" style={{ color: '#93c5fd', fontWeight: 800 }}>
                All frequencies →
              </a>
              </div>
            </div>
          )}
        </div>
      )}

      <style>{`
        @keyframes eos-offline-pulse {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0.3; }
        }
      `}</style>
    </div>
  );
}
